<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { toast } from 'dashboard/composables/useToast';
import { useBrainCopilot } from 'dashboard/composables/useBrainCopilot';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';

// Janela flutuante GLOBAL do copiloto (dockada no canto inferior direito).
// Montada uma vez no Dashboard e escondida por v-show: fechar preserva a
// thread; o agente reabre pelo topbar e continua de onde parou.
const store = useStore();
const route = useRoute();
const { t } = useI18n();
const { uiSettings } = useUISettings();
const { isEnterprise } = useConfig();
const { isOpen, open, close, toggle, markUnread } = useBrainCopilot();

const currentAccountId = useMapGetter('getCurrentAccountId');
const assistants = useMapGetter('captainAssistants/getRecords');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isCaptainAvailable = computed(() => {
  if (!isEnterprise) return false;
  return isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CAPTAIN
  );
});

const copilotFeatureEnabled = computed(() => {
  const account = store.getters['accounts/getAccount'](currentAccountId.value);
  return account?.captain_preferences?.features?.copilot === true;
});

const copilotAvailable = computed(
  () => isCaptainAvailable.value && copilotFeatureEnabled.value
);

// ── Contexto: o que o copiloto esta lendo agora (radar de tela) ──
// Reconhece conversa, deal do CRM e perfil de contato (contexto REAL, enviado
// ao backend) e nomeia as demais telas (so awareness). Cada um so vale quando
// o agente esta DE FATO na rota — nada de contexto stale de tela anterior.
const dealsRecords = useMapGetter('deals/getDeals');
const contactGetter = useMapGetter('contacts/getContact');

// telas conhecidas por prefixo do nome da rota (ordem importa)
const SCREEN_BY_PREFIX = [
  ['crm_reports', 'Relatórios'],
  ['crm', 'CRM / Funil'],
  ['campaigns', 'Campanhas'],
  ['contacts', 'Contatos'],
  ['forms', 'Formulários'],
  ['settings', 'Configurações'],
  ['home', 'Conversas'],
];

const screenContext = computed(() => {
  // conversa aberta
  const routeId = route.params.conversation_id || route.params.conversationId;
  if (
    routeId &&
    currentChat.value?.id &&
    String(currentChat.value.id) === String(routeId)
  ) {
    const chat = currentChat.value;
    const name = chat.meta?.sender?.name;
    return {
      kind: 'conversation',
      payload: { conversation_id: chat.id },
      label: name
        ? t('CONVERSATION_SIDEBAR.BRAIN_CONTEXT_READING', { id: chat.id, name })
        : t('CONVERSATION_SIDEBAR.BRAIN_CONTEXT_READING_NO_NAME', {
            id: chat.id,
          }),
    };
  }
  // deal do CRM
  if (route.name === 'crm_item_detail' && route.params.itemId) {
    const id = Number(route.params.itemId);
    const deal = (dealsRecords.value || []).find(item => item.id === id);
    const name = deal?.title || deal?.contact_name;
    return {
      kind: 'deal',
      payload: { deal_id: id },
      label: name
        ? t('CONVERSATION_SIDEBAR.BRAIN_CONTEXT_DEAL', { id, name })
        : t('CONVERSATION_SIDEBAR.BRAIN_CONTEXT_DEAL_NO_NAME', { id }),
    };
  }
  // perfil de contato
  if (
    String(route.name || '').startsWith('contacts_edit') &&
    route.params.contactId
  ) {
    const id = Number(route.params.contactId);
    const contact = contactGetter.value(id);
    return {
      kind: 'contact',
      payload: { contact_id: id },
      label: t('CONVERSATION_SIDEBAR.BRAIN_CONTEXT_CONTACT', {
        name: contact?.name || `#${id}`,
      }),
    };
  }
  // tela conhecida (so awareness, sem payload)
  const routeName = String(route.name || '');
  const match = SCREEN_BY_PREFIX.find(([prefix]) =>
    routeName.startsWith(prefix)
  );
  if (match) {
    return {
      kind: 'screen',
      payload: {},
      label: t('CONVERSATION_SIDEBAR.BRAIN_CONTEXT_SCREEN', {
        screen: match[1],
      }),
    };
  }
  return {
    kind: 'none',
    payload: {},
    label: t('CONVERSATION_SIDEBAR.BRAIN_CONTEXT_NONE'),
  };
});

const contextLabel = computed(() => screenContext.value.label);
// entidade de verdade sendo lida (chip rosa + contexto enviado ao backend)
const readingEntity = computed(() =>
  ['conversation', 'deal', 'contact'].includes(screenContext.value.kind)
);
const isReadingConversation = computed(
  () => screenContext.value.kind === 'conversation'
);

// ── Thread / mensagens (formato { message_type, message: { content } }) ──
const selectedCopilotThreadId = ref(null);
const expandedToolGroups = ref(new Set());

const messages = computed(() => {
  const items = [];
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  ).forEach(m => {
    const content =
      typeof m.message === 'string'
        ? m.message
        : m.message?.content || m.message?.response || '';
    if (m.message_type === 'assistant_thinking') {
      const last = items.at(-1);
      if (last?.role === 'tools') {
        last.steps.push(content);
      } else {
        items.push({ role: 'tools', steps: [content] });
      }
    } else {
      items.push({ role: m.message_type, content });
    }
  });
  return items;
});

const formatCopilotContent = content =>
  new MessageFormatter(content || '').formattedMessage;

const copilotMessagesRef = ref(null);
watch(
  () => messages.value.length,
  async () => {
    // resposta chegou com a janela fechada -> pontinho no toggle (no-op aberta)
    if (messages.value.at(-1)?.role === 'assistant') markUnread();
    await nextTick();
    const el = copilotMessagesRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  }
);

const toggleToolGroup = idx => {
  const next = new Set(expandedToolGroups.value);
  if (next.has(idx)) next.delete(idx);
  else next.add(idx);
  expandedToolGroups.value = next;
};

const copilotThinking = computed(() => {
  const last = messages.value.at(-1);
  return !!last && (last.role === 'user' || last.role === 'tools');
});

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_captain_assistant_id;
  if (preferredId) {
    const found = assistants.value.find(a => a.id === preferredId);
    if (found) return found;
  }
  if (inboxAssistant.value) {
    const found = assistants.value.find(a => a.id === inboxAssistant.value.id);
    if (found) return found;
  }
  return assistants.value[0];
});

const copilotDraft = ref('');

const sendMessage = async (cannedText = null) => {
  const message = (
    typeof cannedText === 'string' ? cannedText : copilotDraft.value
  )?.trim();
  if (!message) return;
  if (typeof cannedText !== 'string') copilotDraft.value = '';
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        // sem assistente na conta o copiloto roda escopado so na conta
        assistant_id: activeAssistant.value?.id,
        ...screenContext.value.payload,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value?.id,
        ...screenContext.value.payload,
        message,
      });
      selectedCopilotThreadId.value = response.id;
    }
  } catch (error) {
    toast.error(error.message);
  }
};

const insertIntoReply = content => {
  emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content);
};

// ── Acionamento externo (carinha do compositor, menu de selecao) ──
const openFromBus = () => {
  if (!copilotAvailable.value) return;
  open();
};

// Ctrl+. abre/fecha de qualquer tela
const onKeydown = event => {
  if (!copilotAvailable.value) return;
  if (event.ctrlKey && !event.altKey && !event.shiftKey && event.key === '.') {
    event.preventDefault();
    toggle();
  }
};

// trocou de conta: thread e de outra conta, zera
watch(currentAccountId, () => {
  selectedCopilotThreadId.value = null;
  if (isCaptainAvailable.value) store.dispatch('captainAssistants/get');
});

onMounted(() => {
  emitter.on(BUS_EVENTS.OPEN_BRAIN_COPILOT, openFromBus);
  document.addEventListener('keydown', onKeydown);
  if (isCaptainAvailable.value) store.dispatch('captainAssistants/get');
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.OPEN_BRAIN_COPILOT, openFromBus);
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="copilotAvailable"
      v-show="isOpen"
      class="fixed z-40 flex flex-col bg-n-solid-1 border border-n-weak overflow-hidden inset-x-0 bottom-0 h-dvh-safe rounded-none md:inset-x-auto md:bottom-4 md:right-4 md:w-[400px] md:max-w-[calc(100vw-2rem)] md:!h-[560px] md:max-h-[calc(100vh-5rem)] md:rounded-xl md:shadow-[0_12px_28px_rgba(28,25,23,0.18),0_28px_70px_-12px_rgba(28,25,23,0.45)]"
    >
      <!-- Header: carinha + wordmark + contexto + fechar -->
      <div
        class="flex items-center gap-2.5 px-4 py-3 border-b border-n-weak flex-shrink-0"
      >
        <div
          class="size-9 rounded-[10px] bg-gradient-to-br from-n-brand to-n-ruby-9 flex items-center justify-center flex-shrink-0"
        >
          <span class="i-ph-sparkle-fill size-4.5 text-white" />
        </div>
        <div class="flex-1 min-w-0">
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <div class="text-sm font-bold text-n-slate-12 leading-tight">
            br<span class="brain-highlight">ai</span>n
          </div>
          <!-- eslint-enable vue/no-bare-strings-in-template -->
          <div
            class="text-[11px] truncate"
            :class="readingEntity ? 'text-n-ruby-9' : 'text-n-slate-9'"
          >
            {{ contextLabel }}
          </div>
        </div>
        <button
          class="size-7 rounded-md grid place-content-center text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 cursor-pointer transition-colors flex-shrink-0"
          :title="$t('CONVERSATION_SIDEBAR.BRAIN_CLOSE')"
          @click="close"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>

      <!-- Mensagens -->
      <div ref="copilotMessagesRef" class="flex-1 overflow-y-auto">
        <div
          v-if="!messages.length"
          class="flex flex-col items-center justify-center h-full text-center px-4"
        >
          <span class="i-ph-sparkle-fill size-8 text-n-brand/40 mb-3" />
          <p class="text-xs text-n-slate-11 m-0 mb-1">
            {{ $t('CONVERSATION_SIDEBAR.BRAIN_EMPTY_TITLE') }}
          </p>
          <p class="text-[10px] text-n-slate-9 m-0 max-w-[220px]">
            {{ $t('CONVERSATION_SIDEBAR.BRAIN_EMPTY_DESCRIPTION') }}
          </p>
        </div>
        <div v-else class="p-3 space-y-3">
          <template v-for="(msg, idx) in messages" :key="idx">
            <button
              v-if="msg.role === 'tools'"
              class="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2 cursor-pointer transition-colors"
              @click="toggleToolGroup(idx)"
            >
              <span class="i-lucide-settings-2 size-3" />
              {{
                $t('CONVERSATION_SIDEBAR.BRAIN_TOOL_STEPS', {
                  count: msg.steps.length,
                })
              }}
              <span
                class="size-3"
                :class="
                  expandedToolGroups.has(idx)
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
              />
            </button>
            <div
              v-if="msg.role === 'tools' && expandedToolGroups.has(idx)"
              class="pl-3 space-y-0.5"
            >
              <p
                v-for="(step, sIdx) in msg.steps"
                :key="sIdx"
                class="m-0 text-[10px] font-mono text-n-slate-9"
              >
                {{ step }}
              </p>
            </div>
            <div
              v-if="msg.role !== 'tools'"
              class="text-xs p-2.5 rounded-lg"
              :class="
                msg.role === 'assistant' ? 'bg-n-brand/5' : 'bg-n-alpha-2'
              "
            >
              <p v-if="msg.role === 'user'" class="m-0 whitespace-pre-wrap">
                {{ msg.content }}
              </p>
              <div
                v-else
                class="copilot-markdown"
                v-html="formatCopilotContent(msg.content)"
              />
              <button
                v-if="msg.role === 'assistant' && msg.content"
                class="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-n-brand hover:text-n-brand/80 cursor-pointer"
                @click="insertIntoReply(msg.content)"
              >
                <span class="i-lucide-corner-down-left size-3" />
                {{ $t('CONVERSATION_SIDEBAR.BRAIN_INSERT_REPLY') }}
              </button>
            </div>
          </template>
          <div
            v-if="copilotThinking"
            class="flex items-center gap-1.5 text-[11px] text-n-slate-10 px-2.5"
          >
            <span class="i-lucide-loader-2 size-3 animate-spin" />
            {{ $t('CONVERSATION_SIDEBAR.BRAIN_THINKING') }}
          </div>
        </div>
      </div>

      <!-- Acoes rapidas (so fazem sentido lendo uma conversa) -->
      <div
        v-if="isReadingConversation"
        class="flex flex-wrap gap-1.5 px-3 pt-2 border-t border-n-weak flex-shrink-0"
      >
        <button
          class="px-2.5 py-1 rounded-full text-[10px] font-semibold text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 cursor-pointer transition-colors"
          @click="
            sendMessage($t('CONVERSATION_SIDEBAR.BRAIN_PROMPT_SUMMARIZE'))
          "
        >
          {{ $t('CONVERSATION_SIDEBAR.BRAIN_QUICK_SUMMARIZE') }}
        </button>
        <button
          class="px-2.5 py-1 rounded-full text-[10px] font-semibold text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 cursor-pointer transition-colors"
          @click="sendMessage($t('CONVERSATION_SIDEBAR.BRAIN_PROMPT_SUGGEST'))"
        >
          {{ $t('CONVERSATION_SIDEBAR.BRAIN_QUICK_SUGGEST') }}
        </button>
      </div>

      <!-- Input -->
      <div class="p-3 flex-shrink-0">
        <div
          class="flex items-center gap-2 bg-n-alpha-1 rounded-[10px] px-3 py-2"
        >
          <input
            v-model="copilotDraft"
            type="text"
            class="flex-1 text-xs text-n-slate-12 placeholder-n-slate-9 outline-none bg-transparent"
            :placeholder="$t('CONVERSATION_SIDEBAR.BRAIN_INPUT_PLACEHOLDER')"
            @keydown.enter="sendMessage()"
          />
          <button
            class="size-6 rounded-md bg-gradient-to-br from-n-brand to-n-ruby-9 text-white grid place-content-center flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
            @click="sendMessage()"
          >
            <span class="i-lucide-send size-3" />
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.copilot-markdown :deep(p) {
  margin: 0 0 6px;
}
.copilot-markdown :deep(p:last-child) {
  margin-bottom: 0;
}
.copilot-markdown :deep(ul),
.copilot-markdown :deep(ol) {
  margin: 0 0 6px;
  padding-left: 16px;
}
.copilot-markdown :deep(a) {
  color: rgb(var(--n-brand));
  text-decoration: underline;
}
.copilot-markdown :deep(strong) {
  font-weight: 600;
}
</style>
